/** Prices are points before the model multiplier. Pixels are measured from actual bytes. */
export type AiMediaTarget = "image_input" | "image_output" | "audio_input" | "video_input";
export type AiMediaUnit = "image" | "file" | "megapixel" | "pixel_tier" | "million_tokens";
export type AiPixelPriceTier = { maxPixels: number | null; price: number | null };
export type AiMediaPrice = {
  target: AiMediaTarget;
  unit: AiMediaUnit;
  price: number | null;
  cachedPrice?: number | null;
  cacheWritePrice?: number | null;
  /** Free images per upstream request, before applying the rate. */
  freeImages: number;
  /** Zero means exact megapixels; otherwise round each image up to this increment. */
  megapixelStep: number;
  megapixelPixels: 1000000 | 1048576;
  megapixelAggregation: "image" | "request";
  /** A flat per-image price includes this many megapixels; extra pixels use price. */
  basePrice: number;
  includedMegapixels: number;
  /** Replace the image portion of the base token charge, or charge an explicit surcharge. */
  tokenMode: "additional" | "replace";
  tiers: AiPixelPriceTier[];
};

export type AiMediaUsage = {
  inputImages?: { pixels?: number }[];
  outputImages?: { pixels?: number }[];
  inputImageTokens?: number;
  outputImageTokens?: number;
  outputAudioTokens?: number;
  outputVideoTokens?: number;
  outputAudios?: { seconds?: number }[];
  outputVideos?: { seconds?: number }[];
  cachedImageTokens?: number;
  cacheWriteImageTokens?: number;
  inputAudios?: { bytes?: number; seconds?: number }[];
  inputVideos?: { bytes?: number; seconds?: number; hasAudio?: boolean }[];
  inputAudioTokens?: number;
  inputVideoTokens?: number;
  cachedAudioTokens?: number;
  cachedVideoTokens?: number;
  cacheWriteAudioTokens?: number;
  cacheWriteVideoTokens?: number;
};

export const INPUT_MEDIA_FIELDS = {
  image_input: { label: "图片", files: "inputImages", tokens: "inputImageTokens", cached: "cachedImageTokens", write: "cacheWriteImageTokens" },
  audio_input: { label: "音频", files: "inputAudios", tokens: "inputAudioTokens", cached: "cachedAudioTokens", write: "cacheWriteAudioTokens" },
  video_input: { label: "视频", files: "inputVideos", tokens: "inputVideoTokens", cached: "cachedVideoTokens", write: "cacheWriteVideoTokens" },
} as const;

export function mediaTargetLabel(target: AiMediaTarget): string {
  return target === "image_output" ? "图片输出" : `${INPUT_MEDIA_FIELDS[target].label}输入`;
}

export const AI_MEDIA_TARGETS = [
  { value: "image_input", label: "图片输入" },
  { value: "image_output", label: "图片输出" },
  { value: "audio_input", label: "音频输入" },
  { value: "video_input", label: "视频输入" },
] satisfies { value: AiMediaTarget; label: string }[];
export const AI_MEDIA_UNITS = [
  { value: "image", label: "每张" },
  { value: "file", label: "每份" },
  { value: "pixel_tier", label: "像素区间 / 张" },
  { value: "megapixel", label: "每百万像素" },
  { value: "million_tokens", label: "每百万图片 Token" },
] satisfies { value: AiMediaUnit; label: string }[];

export function newMediaPrice(target: AiMediaTarget = "image_input"): AiMediaPrice {
  const audiovisual = target === "audio_input" || target === "video_input";
  return { target, unit: audiovisual ? "file" : "image", price: null, freeImages: 0, megapixelStep: 0, megapixelPixels: 1000000, megapixelAggregation: "image", basePrice: 0, includedMegapixels: 0, tokenMode: audiovisual ? "replace" : "additional", tiers: [] };
}

export function validPixelPriceTiers(tiers: readonly AiPixelPriceTier[]): boolean {
  return tiers.length > 0 && tiers.length <= 50 && tiers.every((tier, index) =>
    tier.maxPixels === null ? index === tiers.length - 1
      : Number.isSafeInteger(tier.maxPixels) && tier.maxPixels > (index ? tiers[index - 1].maxPixels ?? Infinity : 0),
  ) && tiers.at(-1)?.maxPixels === null;
}

export function mediaPricingError(items: readonly AiMediaPrice[]): string | null {
  if (items.length > 4 || new Set(items.map(item => item.target)).size !== items.length) return "同一媒体项目只能设置一条计费规则";
  const validPrice = (value: unknown) => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 2_147_483_647 && Math.abs(value * 1e6 - Math.round(value * 1e6)) < 0.00001;
  for (const item of items) {
    if (!Array.isArray(item.tiers) || item.tiers.some(tier => !tier || typeof tier !== "object")) return "图片像素区间无效";
    if (!AI_MEDIA_TARGETS.some(option => option.value === item.target) || !AI_MEDIA_UNITS.some(option => option.value === item.unit)) return "媒体计费项目或单位无效";
    const audiovisual = item.target === "audio_input" || item.target === "video_input";
    if (audiovisual && (!["file", "million_tokens"].includes(item.unit) || item.tokenMode !== "replace" || item.freeImages !== 0 || item.basePrice !== 0 || item.includedMegapixels !== 0 || item.megapixelStep !== 0 || item.tiers.length)) return "音频、视频输入仅支持每份或 M Token 计费";
    if (!audiovisual && item.unit === "file") return "图片计费单位无效";
    if (!Number.isSafeInteger(item.freeImages) || item.freeImages < 0 || item.freeImages > 1000) return "免费图片数量须为 0 至 1000 的整数";
    if (!Number.isFinite(item.megapixelStep) || item.megapixelStep < 0 || item.megapixelStep > 1000) return "像素计费步长无效";
    if (![1000000, 1048576].includes(item.megapixelPixels) || !validPrice(item.basePrice) || !Number.isFinite(item.includedMegapixels) || item.includedMegapixels < 0 || item.includedMegapixels > 1000) return "百万像素基数或基础费用无效";
    if (!["image", "request"].includes(item.megapixelAggregation)) return "像素计费范围无效";
    if (!["additional", "replace"].includes(item.tokenMode)) return "图片 Token 处理方式无效";
    if ([item.cachedPrice, item.cacheWritePrice].some(value => value !== undefined && value !== null && !validPrice(value))) return "图片缓存积分单价无效";
    if (item.unit === "million_tokens" && item.freeImages !== 0) return "图片 Token 计费不能设置免费张数";
    if (item.unit === "pixel_tier") {
      if (!validPixelPriceTiers(item.tiers)) return "像素上限须依次递增，末行留空表示不限";
      if (item.tiers.some(tier => !validPrice(tier.price))) return "请填写每个像素区间的积分单价";
    } else if (!validPrice(item.price)) return "请填写媒体计费积分单价，免费请填 0";
  }
  return null;
}

/** Reads persisted configuration without inventing missing paid rates. Save validation is strict. */
export function normalizeMediaPrices(value: unknown): AiMediaPrice[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) throw new Error("媒体计费配置无效");
  const items = value.map(raw => ({ ...newMediaPrice(), ...raw })) as AiMediaPrice[];
  const error = mediaPricingError(items);
  if (error) throw new Error(error);
  return items;
}

export function publicMediaPrices(items: AiMediaPrice[] | undefined, multiply: (price: number | null) => number | null): AiMediaPrice[] | undefined {
  return items?.map(item => ({ ...item, price: multiply(item.price), basePrice: multiply(item.basePrice)!,
    ...(item.cachedPrice === undefined ? {} : { cachedPrice: multiply(item.cachedPrice) }),
    ...(item.cacheWritePrice === undefined ? {} : { cacheWritePrice: multiply(item.cacheWritePrice) }),
    tiers: item.tiers.map(tier => ({ ...tier, price: multiply(tier.price) })) }));
}
